import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const LAST_UPDATED = 'April 29, 2026';

const SECTIONS = [
  { id: 'introduction',    label: 'Introduction' },
  { id: 'eligibility',     label: 'Eligibility' },
  { id: 'accounts',        label: 'Accounts & Registration' },
  { id: 'services',        label: 'Our Services' },
  { id: 'disclaimer',      label: 'Educational Disclaimer' },
  { id: 'payments',        label: 'Payments & Refunds' },
  { id: 'conduct',         label: 'User Conduct' },
  { id: 'ip',              label: 'Intellectual Property' },
  { id: 'termination',     label: 'Termination' },
  { id: 'liability',       label: 'Limitation of Liability' },
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'governing-law',   label: 'Governing Law' },
  { id: 'changes',         label: 'Changes to Terms' },
  { id: 'contact',         label: 'Contact Us' },
];

const TermsOfService = () => {
  const [active, setActive] = useState('introduction');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Seo
        title="Terms of Service — InvFly"
        description="Read InvFly's Terms of Service governing the use of our educational platform."
      />

      {/* Hero */}
      <div className="ph">
        <div className="container">
          <div className="ph-tag">Legal</div>
          <div className="ph-title">Terms of <span className="txt-ac">Service</span></div>
          <p className="ph-desc">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <section className="sec">
        <div className="container">
          <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start' }}>

            {/* Sticky Table of Contents */}
            <aside style={{
              width: '220px',
              flexShrink: 0,
              position: 'sticky',
              top: '88px',
              alignSelf: 'flex-start',
              display: 'none',
            }} className="policy-toc">
              <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '12px' }}>Contents</div>
              <nav>
                {SECTIONS.map(s => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', background: 'none',
                      border: 'none', borderLeft: `2px solid ${active === s.id ? 'var(--accent)' : 'var(--border)'}`,
                      padding: '6px 12px', fontSize: '13px', cursor: 'pointer',
                      color: active === s.id ? 'var(--accent)' : 'var(--text-2)',
                      fontFamily: 'var(--fb)', fontWeight: active === s.id ? '600' : '400',
                      transition: 'color 0.15s, border-color 0.15s',
                      marginBottom: '2px',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Main Content */}
            <div style={{ flex: 1, maxWidth: '760px' }}>

              {/* SEBI Banner */}
              <div style={{
                background: 'rgba(245,166,35,0.08)',
                border: '1px solid rgba(245,166,35,0.35)',
                borderLeft: '4px solid #F5A623',
                borderRadius: '8px',
                padding: '14px 18px',
                marginBottom: '32px',
                fontSize: '13.5px',
                color: 'var(--text-2)',
                lineHeight: '1.7',
              }}>
                ⚠ <strong style={{ color: '#F5A623' }}>Important:</strong> InvFly provides <strong style={{ color: 'var(--text)' }}>educational content only</strong>. We are <strong style={{ color: 'var(--text)' }}>not registered with SEBI</strong> and do not provide investment advice, portfolio management, or stock recommendations. Nothing on this platform should be construed as financial advice.
              </div>

              <PolicySection id="introduction" title="1. Introduction">
                <p>Welcome to InvFly. These Terms of Service ("<strong>Terms</strong>") govern your access to and use of the InvFly website, mobile applications, and all related services (collectively, the "<strong>Platform</strong>") operated by InvFly, Hyderabad, Telangana, India.</p>
                <p>By registering for an account, enrolling in a course, or otherwise using the Platform, you agree to be bound by these Terms and our <Link to="/privacypolicy" style={{ color: 'var(--accent)' }}>Privacy Policy</Link>. If you do not agree, please do not use the Platform.</p>
              </PolicySection>

              <PolicySection id="eligibility" title="2. Eligibility">
                <p>By using InvFly, you represent that:</p>
                <ul>
                  <li>You are at least <strong>18 years of age</strong>, or using the Platform under the supervision of a parent or legal guardian who agrees to these Terms.</li>
                  <li>You have the legal capacity to enter into a binding agreement.</li>
                  <li>You are not barred from accessing or using our services under any applicable law.</li>
                </ul>
              </PolicySection>

              <PolicySection id="accounts" title="3. Accounts &amp; Registration">
                <p>To access certain features you must create an account. You agree to:</p>
                <ul>
                  <li>Provide accurate, current, and complete information during registration.</li>
                  <li>Verify your email address via OTP before completing registration.</li>
                  <li>Maintain the confidentiality of your account credentials and not share them with third parties.</li>
                  <li>Notify us immediately if you suspect unauthorised use of your account.</li>
                  <li>Be responsible for all activity that occurs under your account.</li>
                </ul>
                <p>We reserve the right to suspend or terminate accounts that provide false information or violate these Terms.</p>
              </PolicySection>

              <PolicySection id="services" title="4. Our Services">
                <p>InvFly offers the following:</p>
                <ul>
                  <li><strong>Online courses</strong> covering stock market education, technical analysis, fundamental analysis, options, and related financial topics.</li>
                  <li><strong>Blog articles</strong> — educational and informational in nature.</li>
                  <li><strong>Tools and resources</strong> for learning and practice.</li>
                  <li><strong>Community and mentorship</strong> access included in certain course tiers.</li>
                </ul>
                <p>We reserve the right to modify, suspend, or discontinue any part of the Platform at any time with reasonable notice.</p>
              </PolicySection>

              <PolicySection id="disclaimer" title="5. Educational Disclaimer">
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--accent)',
                  borderRadius: '8px',
                  padding: '14px 18px',
                  marginBottom: '16px',
                }}>
                  <p style={{ margin: 0 }}><strong style={{ color: 'var(--text)' }}>InvFly is strictly an educational platform.</strong> All course content, articles, market analyses, and materials published on this Platform are intended solely for educational and informational purposes.</p>
                </div>
                <ul>
                  <li>InvFly is <strong>not a SEBI-registered Investment Adviser</strong>, Research Analyst, or Portfolio Manager.</li>
                  <li>Nothing on this Platform constitutes or should be construed as investment advice, a recommendation to buy or sell any security, or a solicitation of any investment decision.</li>
                  <li>Past performance discussed in course materials is illustrative and <strong>not indicative of future results</strong>.</li>
                  <li>All investments in securities markets are subject to market risks. <strong>Please read all scheme-related documents carefully before investing.</strong></li>
                  <li>InvFly is not liable for any investment losses you may incur based on information obtained from our Platform.</li>
                </ul>
              </PolicySection>

              <PolicySection id="payments" title="6. Payments &amp; Refunds">
                <SubHeading>6.1 Pricing</SubHeading>
                <p>Course fees are displayed on the respective course pages in Indian Rupees (INR). All prices are inclusive of applicable taxes unless stated otherwise. InvFly reserves the right to change prices at any time; changes will not affect existing enrolments.</p>
                <SubHeading>6.2 Payment</SubHeading>
                <p>Payments are processed by our payment gateway partner. By making a payment you agree to their terms. InvFly does not store card or bank account details.</p>
                <SubHeading>6.3 Refund Policy</SubHeading>
                <p>Given the digital and educational nature of our content:</p>
                <ul>
                  <li>Refunds may be requested within <strong>7 days of purchase</strong>, provided you have not accessed more than 20% of the course content.</li>
                  <li>Refund requests after this window, or where significant course content has been accessed, will be evaluated on a case-by-case basis at our sole discretion.</li>
                  <li>No refunds are provided for subscription renewals once the renewal period has commenced.</li>
                  <li>Approved refunds will be credited to the original payment method within 7–10 business days.</li>
                </ul>
                <p>To request a refund, contact us via the <Link to="/contact" style={{ color: 'var(--accent)' }}>Contact page</Link> with your registered email and payment reference.</p>
              </PolicySection>

              <PolicySection id="conduct" title="7. User Conduct">
                <p>You agree not to:</p>
                <ul>
                  <li>Use the Platform for any unlawful purpose or in violation of any applicable law.</li>
                  <li>Share, redistribute, resell, or publicly broadcast any paid course content.</li>
                  <li>Attempt to reverse-engineer, scrape, or copy any part of the Platform's content or code.</li>
                  <li>Impersonate any person or entity, or misrepresent your affiliation.</li>
                  <li>Upload or transmit malicious code, spam, or harmful content.</li>
                  <li>Use automated tools or bots to access the Platform without our prior written consent.</li>
                  <li>Circumvent, disable, or interfere with security-related features of the Platform.</li>
                </ul>
              </PolicySection>

              <PolicySection id="ip" title="8. Intellectual Property">
                <p>All content on InvFly — including but not limited to course videos, slides, articles, graphics, logos, and software — is the exclusive intellectual property of InvFly or its licensors and is protected under Indian copyright, trademark, and other applicable laws.</p>
                <p><strong>Upon purchasing a course, you are granted a limited, non-exclusive, non-transferable, personal licence</strong> to access and view the course content for your own educational use. This licence does not include the right to:</p>
                <ul>
                  <li>Download, copy, or reproduce content (except where explicitly permitted).</li>
                  <li>Share login credentials or course access with others.</li>
                  <li>Use InvFly branding or content for commercial purposes.</li>
                </ul>
              </PolicySection>

              <PolicySection id="termination" title="9. Termination">
                <p>We reserve the right to suspend or terminate your account and access to the Platform at any time, with or without notice, if:</p>
                <ul>
                  <li>You breach any provision of these Terms.</li>
                  <li>We are required to do so by law.</li>
                  <li>We discontinue the Platform or a particular service.</li>
                </ul>
                <p>You may delete your account at any time by contacting us. Upon termination, your licence to access course content ends. Provisions of these Terms that by their nature should survive termination (including Sections 5, 8, 10, and 11) will remain in effect.</p>
              </PolicySection>

              <PolicySection id="liability" title="10. Limitation of Liability">
                <p>To the fullest extent permitted by applicable law:</p>
                <ul>
                  <li>InvFly and its officers, directors, employees, and instructors will not be liable for any <strong>indirect, incidental, special, consequential, or punitive damages</strong>, including financial losses arising from your use of or inability to use the Platform.</li>
                  <li>Our total liability to you for any claim arising out of or relating to these Terms or the Platform will not exceed the <strong>amount you paid to InvFly in the 12 months preceding the claim</strong>.</li>
                  <li>The Platform is provided on an "<strong>as is</strong>" and "<strong>as available</strong>" basis without warranties of any kind, express or implied.</li>
                </ul>
              </PolicySection>

              <PolicySection id="indemnification" title="11. Indemnification">
                <p>You agree to indemnify, defend, and hold harmless InvFly, its instructors, employees, and partners from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of:</p>
                <ul>
                  <li>Your use of the Platform in violation of these Terms.</li>
                  <li>Your violation of any third-party right, including intellectual property or privacy rights.</li>
                  <li>Any investment or financial decision you make based on content from our Platform.</li>
                </ul>
              </PolicySection>

              <PolicySection id="governing-law" title="12. Governing Law &amp; Dispute Resolution">
                <p>These Terms are governed by the laws of <strong>India</strong>. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in <strong>Hyderabad, Telangana, India</strong>.</p>
                <p>We encourage you to contact us first to resolve any dispute informally before initiating legal proceedings.</p>
              </PolicySection>

              <PolicySection id="changes" title="13. Changes to These Terms">
                <p>We may update these Terms at any time. We will notify you of material changes by updating the "Last updated" date and, where appropriate, by email or a notice on the Platform.</p>
                <p>Your continued use of InvFly after changes are posted constitutes your acceptance of the revised Terms.</p>
              </PolicySection>

              <PolicySection id="contact" title="14. Contact Us">
                <p>If you have questions about these Terms, please contact us:</p>
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--accent)',
                  borderRadius: '8px',
                  padding: '16px 20px',
                  marginTop: '12px',
                }}>
                  <p style={{ margin: '0 0 4px' }}><strong>InvFly</strong></p>
                  <p style={{ margin: '0 0 4px' }}>Hyderabad, Telangana, India</p>
                  <p style={{ margin: '0' }}>
                    <Link to="/contact" style={{ color: 'var(--accent)' }}>Contact Form</Link>
                  </p>
                </div>
              </PolicySection>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const PolicySection = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: '44px', scrollMarginTop: '96px' }}>
    <h2 style={{
      fontSize: '19px', fontWeight: '700', color: 'var(--text)',
      fontFamily: 'var(--fd)', marginBottom: '14px',
      paddingBottom: '10px', borderBottom: '1px solid var(--border)',
    }}>
      {title}
    </h2>
    <div style={{ fontSize: '14.5px', color: 'var(--text-2)', lineHeight: '1.85' }}>
      {children}
    </div>
  </section>
);

const SubHeading = ({ children }) => (
  <p style={{ fontWeight: '600', color: 'var(--text)', marginTop: '14px', marginBottom: '6px', fontSize: '14.5px' }}>
    {children}
  </p>
);

export default TermsOfService;
